import React, {useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {FacetPillSkeletonLoader} from 'components/Filters/FacetFilter/FacetsRenderPills'
import {filterMessages} from 'components/Filters/filterMessages'
import {
  getIsFilterSelected,
  getPriceActiveLabel
} from 'components/Filters/utils'
import {useFreezableFilter} from 'hooks/useFreezableFilter'
import {usePriceFilter} from 'hooks/usePriceFilter'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getFacetFilters,
  getNeighborhoods,
  getPropertyBrands
} from 'modules/sapiSearch/selectors'
import {searchParamToFacet} from 'modules/search/constants'
import {getIsCheckInAtLeastTwoDaysAway} from 'modules/search/selectors'
import {FilterUrlParams} from 'modules/search/types'

import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Pill} from '@vio/atlas/src/components/designSystem/Pill'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@vio/core/src/hotel/reviewSentiments'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'

import {RecentlyUsedFiltersProps} from './RecentlyUsedFilters'

interface Props
  extends Pick<
    RecentlyUsedFiltersProps,
    'toggleMultiFilter' | 'toggleFilters' | 'filters' | 'appliedFilters'
  > {
  filterValue: string
  type: keyof FilterUrlParams | 'price'
}
export const StoredFilterItem: React.FC<Props> = ({
  filters,
  appliedFilters,
  filterValue,
  type,
  toggleMultiFilter,
  toggleFilters
}) => {
  const toggle = useToggle()
  const {formatMessage} = useIntl()
  const {brandName} = useBrand()
  const {
    monetisation: {onlyOnBrandTag}
  } = useBrandCapabilities()
  const {getTotalPriceFilter} = usePriceFilter()
  const facetFilters = useSelector(getFacetFilters)

  const currencyCode = useSelector(getCurrencyCode)
  const neighborhoods = useSelector(getNeighborhoods)
  const propertyBrands = useSelector(getPropertyBrands)
  const isCheckInAtLeastTwoDaysAway = useSelector(
    getIsCheckInAtLeastTwoDaysAway
  )

  const facilitiesKey = searchParamToFacet.facilities.param
  const themesKey = searchParamToFacet.themes.param
  const propertyTypesKey = searchParamToFacet.propertyTypes.param
  const starRatingKey = searchParamToFacet.starRatings.param

  const isDealFreezeFilterEnabled = useFreezableFilter() // Added as a part of c747927f-deal-freeze-srp

  const priceCurrencySymbol = useMemo(() => {
    const enabledCurrencies = getEnabledCurrencies(
      process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
    )
    const currency = enabledCurrencies.find(
      currency => currency.code === currencyCode
    )
    return currency?.symbol ?? ''
  }, [currencyCode])

  const getPriceLabel = useCallback(
    (minPrice, maxPrice) => {
      return getPriceActiveLabel({
        minPrice: getTotalPriceFilter(minPrice),
        maxPrice: getTotalPriceFilter(maxPrice),
        currencySymbol: priceCurrencySymbol,
        formatMessage
      })
    },
    [priceCurrencySymbol, formatMessage, getTotalPriceFilter]
  )

  const getNormalizedPriceNumber = (price: string | number) => {
    if (!price) return undefined
    if (price === 'undefined') return undefined
    const priceNumber = Number(price)
    return !isNaN(priceNumber) ? priceNumber : undefined
  }

  const getNeighborhoodName = useCallback(
    (neighborhoodId: string) => {
      return (
        neighborhoods?.find(neighborhood => neighborhood.id === neighborhoodId)
          ?.value ?? ''
      )
    },
    [neighborhoods]
  )
  const getPropertyBrandName = useCallback(
    (propertyBrandId: string) => {
      return (
        propertyBrands?.find(
          propertyBrand => propertyBrand.id === propertyBrandId
        )?.value ?? ''
      )
    },
    [propertyBrands]
  )

  const priceRange = useMemo(() => {
    if (type !== 'price') return {minPrice: undefined, maxPrice: undefined}
    const [minPrice, maxPrice] = filterValue.split('-')
    return {
      minPrice: getNormalizedPriceNumber(minPrice),
      maxPrice: getNormalizedPriceNumber(maxPrice)
    }
  }, [filterValue, type])

  const isSelectedPriceFilter = useMemo(() => {
    return (
      getNormalizedPriceNumber(filters.priceMin) === priceRange.minPrice &&
      getNormalizedPriceNumber(filters.priceMax) === priceRange.maxPrice
    )
  }, [filters, priceRange])

  const reviewScoreLabel = useMemo(() => {
    if (type !== 'reviewScore' || !filterValue) return null

    const sentimentMessageKey = getReviewSentimentKey(Number(filterValue))
    const translatedSentiment = sentimentMessageKey
      ? reviewSentimentMessages[sentimentMessageKey]
      : null
    return `${formatMessage(translatedSentiment)} ${filterValue}+`
  }, [filterValue, formatMessage, type])

  switch (type) {
    case 'facilities': {
      const item = facetFilters.facilities?.[filterValue]
      const isDisabled =
        !item?.isAvailable &&
        !getIsFilterSelected(appliedFilters.facilities, filterValue)

      if (!item) return null
      if (!item.label) return <FacetPillSkeletonLoader />

      return (
        <Pill
          size="lg"
          isSelected={getIsFilterSelected(filters.facilities, filterValue)}
          isDisabled={isDisabled}
          onClick={() =>
            toggleMultiFilter(
              facilitiesKey as keyof FilterUrlParams,
              filterValue
            )
          }
        >
          {item.label}
        </Pill>
      )
    }
    case 'themes': {
      const item = facetFilters.themes?.[filterValue]
      const isDisabled =
        !item?.isAvailable &&
        !getIsFilterSelected(appliedFilters.themes, filterValue)

      if (!item) return null
      if (!item.label) return <FacetPillSkeletonLoader />

      return (
        <Pill
          size="lg"
          isSelected={getIsFilterSelected(filters.themes, filterValue)}
          isDisabled={isDisabled}
          onClick={() =>
            toggleMultiFilter(themesKey as keyof FilterUrlParams, filterValue)
          }
        >
          {item.label}
        </Pill>
      )
    }

    case 'propertyTypes': {
      const item = facetFilters.propertyTypes?.[filterValue]
      const isDisabled =
        !item?.isAvailable &&
        !getIsFilterSelected(appliedFilters.propertyTypes, filterValue)

      if (!item) return null
      if (!item.label) return <FacetPillSkeletonLoader />

      return (
        <Pill
          size="lg"
          isSelected={getIsFilterSelected(filters.propertyTypes, filterValue)}
          isDisabled={isDisabled}
          onClick={() =>
            toggleMultiFilter(
              propertyTypesKey as keyof FilterUrlParams,
              filterValue
            )
          }
        >
          {item.label}
        </Pill>
      )
    }
    case 'starRatings': {
      const isDisabled =
        !facetFilters.starRatings?.[filterValue]?.isAvailable &&
        !getIsFilterSelected(appliedFilters.starRatings, filterValue)

      return (
        <Pill
          size="lg"
          iconStart={<Icon name={'Star'} />}
          isSelected={getIsFilterSelected(filters.starRatings, filterValue)}
          isDisabled={isDisabled}
          onClick={() =>
            toggleMultiFilter(
              starRatingKey as keyof FilterUrlParams,
              filterValue
            )
          }
        >
          {formatMessage(
            {
              id: 'filters.starRating',
              defaultMessage:
                '{starRating, plural, one {1‑star} other {#‑star}}'
            },
            {starRating: filterValue}
          )}
        </Pill>
      )
    }
    case 'price':
      return (
        <Pill
          size="lg"
          isSelected={isSelectedPriceFilter}
          onClick={() =>
            toggleFilters({
              priceMin: isSelectedPriceFilter ? undefined : priceRange.minPrice,
              priceMax: isSelectedPriceFilter ? undefined : priceRange.maxPrice
            })
          }
        >
          {getPriceLabel(priceRange.minPrice, priceRange.maxPrice)}
        </Pill>
      )
    case 'brandIds':
      return (
        <Pill
          size="lg"
          isSelected={getIsFilterSelected(filters.brandIds, filterValue)}
          onClick={() => toggleMultiFilter('brandIds', filterValue)}
        >
          {getPropertyBrandName(filterValue)}
        </Pill>
      )
    case 'neighborhoodIds':
      return (
        <Pill
          size="lg"
          isSelected={getIsFilterSelected(filters.neighborhoodIds, filterValue)}
          onClick={() => toggleMultiFilter('neighborhoodIds', filterValue)}
        >
          {getNeighborhoodName(filterValue)}
        </Pill>
      )
    case 'amenities':
      return (
        <Pill
          size="lg"
          isSelected={getIsFilterSelected(filters.amenities, filterValue)}
          onClick={() => toggleMultiFilter('amenities', filterValue)}
        >
          {formatMessage(filterMessages[filterValue])}
        </Pill>
      )
    case 'reviewScore': {
      const isSelected = filters.reviewScore?.includes(filterValue)
      return (
        <Pill
          size="lg"
          isSelected={isSelected}
          onClick={() =>
            toggleFilters({
              reviewScore: isSelected ? undefined : filterValue
            })
          }
        >
          {reviewScoreLabel}
        </Pill>
      )
    }
    case 'freeCancellation': {
      const isSelected = filters.freeCancellation === '1'
      const isDisabled =
        !isCheckInAtLeastTwoDaysAway && appliedFilters.freeCancellation !== '1'

      return (
        <Pill
          size="lg"
          isSelected={isSelected}
          isDisabled={isDisabled}
          onClick={() =>
            toggleFilters({
              freeCancellation: isSelected ? undefined : '1'
            })
          }
        >
          {formatMessage(filterMessages.freeCancellationTitle)}
        </Pill>
      )
    }
    case 'payLater': {
      const isSelectedPayLater = filters.payLater === '1'
      const isDisabled =
        !isCheckInAtLeastTwoDaysAway && appliedFilters.freeCancellation !== '1'

      return (
        <Pill
          size="lg"
          isSelected={isSelectedPayLater}
          isDisabled={isDisabled}
          onClick={() =>
            toggleFilters({
              payLater: isSelectedPayLater ? undefined : '1'
            })
          }
        >
          {formatMessage(filterMessages.payLater)}
        </Pill>
      )
    }

    case 'exclusive': {
      if (!onlyOnBrandTag.enabled) return null
      const isSelectedExclusive = filters.exclusive === '1'
      return (
        <Pill
          size="lg"
          isSelected={isSelectedExclusive}
          onClick={() =>
            toggleFilters({
              exclusive: isSelectedExclusive ? undefined : '1'
            })
          }
        >
          {formatMessage(onlyOnBrandTag.config.message, {brandName})}
        </Pill>
      )
    }

    // Added as a part of c747927f-deal-freeze-srp
    case 'dealFreeze': {
      const isSelected = filters.dealFreeze === '1'
      const isDisabled =
        !isDealFreezeFilterEnabled && appliedFilters.dealFreeze !== '1'

      if (toggle('c747927f-deal-freeze-srp', true, false)) return null

      return (
        <Pill
          size="lg"
          isSelected={isSelected}
          isDisabled={isDisabled}
          onClick={() =>
            toggleFilters({
              dealFreeze: isSelected ? undefined : '1'
            })
          }
        >
          {formatMessage(filterMessages.freezable)}
        </Pill>
      )
    }

    default:
      return null
  }
}
